import { useEffect, useRef, useState } from 'react';
import { EllipsisVertical, RotateCcw, MessageSquare, Loader2, Check } from 'lucide-react';

export interface ConversationSummary {
  /** interactionId of the past conversation. */
  id: string;
  /** Short title — typically the first user message. */
  title: string;
  /** Last-activity timestamp (ms) for relative-time display. */
  updatedAt?: number;
}

interface ChatMenuProps {
  onRestart: () => void;
  restartLabel: string;
  /**
   * Past conversations for this visitor, loaded on demand when the menu opens
   * (ChatGPT-style). Undefined → the section is hidden entirely (e.g. anon with
   * no history, or the feature isn't wired for this embed).
   */
  conversations?: ConversationSummary[];
  conversationsLoading?: boolean;
  currentConversationId?: string | null;
  /** Fired when the menu opens — the parent lazy-loads the conversation list. */
  onOpen?: () => void;
  onSelectConversation?: (id: string) => void;
  previousLabel?: string;
  emptyLabel?: string;
}

function relativeTime(ts?: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return `${Math.round(day / 7)}w ago`;
}

/**
 * Header kebab menu — the hub for conversation controls (Intercom/ChatGPT
 * pattern). Zero-dep dropdown (no Radix) to keep the iframe bundle small.
 * Closes on click-outside + Esc.
 */
export function ChatMenu({
  onRestart,
  restartLabel,
  conversations,
  conversationsLoading,
  currentConversationId,
  onOpen,
  onSelectConversation,
  previousLabel = 'Previous conversations',
  emptyLabel = 'No previous conversations',
}: ChatMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    onOpen?.();
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
    // onOpen is intentionally not a dep — we call it once per open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showConversations = conversations !== undefined || conversationsLoading;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Conversation menu"
        data-testid="chat-menu-button"
        onClick={() => setOpen((o) => !o)}
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground dark:text-white/30 dark:hover:bg-white/[0.08]"
      >
        <EllipsisVertical className="size-5" strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="chat-menu"
          className="absolute right-0 top-10 z-40 w-64 overflow-hidden rounded-xl border border-border bg-popover py-1.5 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150 dark:border-white/[0.1] dark:bg-[#1e2836]"
        >
          {showConversations && (
            <div className="max-h-64 overflow-y-auto">
              <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70 dark:text-white/40">
                {previousLabel}
              </p>
              {conversationsLoading && (
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground dark:text-white/50">
                  <Loader2 className="size-3.5 animate-spin" />
                  Loading…
                </div>
              )}
              {!conversationsLoading && conversations && conversations.length === 0 && (
                <p className="px-3 py-2 text-sm text-muted-foreground dark:text-white/40">{emptyLabel}</p>
              )}
              {!conversationsLoading &&
                conversations?.map((c) => {
                  const isCurrent = c.id === currentConversationId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="menuitem"
                      data-testid="conversation-item"
                      onClick={() => {
                        if (!isCurrent) onSelectConversation?.(c.id);
                        setOpen(false);
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent dark:text-white/85 dark:hover:bg-white/[0.06]"
                    >
                      <MessageSquare className="size-3.5 shrink-0 text-muted-foreground dark:text-white/40" />
                      <span className="min-w-0 flex-1 truncate">{c.title || 'Conversation'}</span>
                      {isCurrent ? (
                        <Check className="size-3.5 shrink-0 text-primary" />
                      ) : (
                        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/60 dark:text-white/30">
                          {relativeTime(c.updatedAt)}
                        </span>
                      )}
                    </button>
                  );
                })}
              <div className="my-1 h-px bg-border dark:bg-white/[0.08]" />
            </div>
          )}

          <button
            type="button"
            role="menuitem"
            data-testid="menu-restart"
            onClick={() => {
              setOpen(false);
              onRestart();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent dark:text-white/85 dark:hover:bg-white/[0.06]"
          >
            <RotateCcw className="size-4 shrink-0 text-muted-foreground dark:text-white/40" strokeWidth={2} />
            {restartLabel}
          </button>
        </div>
      )}
    </div>
  );
}
